import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { TimeField } from '@mui/x-date-pickers';

import { callApi } from 'utils';

const fullDay = ['00:00', '23:59'];
const toTimeValue = (time) => dayjs(`2022-02-22 ${time}`);

function getEffectiveDayparts(job, dateKey) {
  return job?.dayparts?.[dateKey] || job?.dayparts?.default || fullDay;
}

const BatchTodayDaypartsDialog = ({jobs, excludedCount=0, onClose, onSaved}) => {
  const today = dayjs().tz();
  const todayKey = today.format('YYYYMMDD');
  const effectiveDayparts = useMemo(
    () => jobs.map(job => getEffectiveDayparts(job, todayKey)),
    [jobs, todayKey]
  );
  const initialDayparts = effectiveDayparts[0] || fullDay;
  const [start, setStart] = useState(() => toTimeValue(initialDayparts[0]));
  const [end, setEnd] = useState(() => toTimeValue(initialDayparts[1]));
  const [saving, setSaving] = useState(false);

  const mixed = new Set(effectiveDayparts.map(dayparts => dayparts.join('-'))).size > 1;
  const startText = start?.isValid() ? start.format('HH:mm') : '';
  const endText = end?.isValid() ? end.format('HH:mm') : '';
  const invalid = !startText || !endText;

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (invalid || saving || jobs.length === 0) return;

    setSaving(true);
    const ids = jobs.map(job => job.id).join(',');
    callApi(`/jobs/batch/today-dayparts?ids=${ids}`, 'PUT', {
      start_time: startText,
      end_time: endText,
    }).then(onSaved).catch(() => setSaving(false));
  };

  return (
    <Dialog open={true} onClose={saving ? undefined : onClose}>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>批量修改当天时段</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{pt: 0.5, minWidth: 360}}>
            <Typography variant="overline" sx={{textTransform: 'none'}}>
              修改日期：{today.format('MM-DD')}
            </Typography>
            <Typography variant="body2">将统一修改 {jobs.length} 个任务今天的投放时段。</Typography>
            {mixed && (
              <Alert severity="warning">
                所选任务当前时段不一致，保存后将统一覆盖为以下当天时段。
              </Alert>
            )}
            {excludedCount > 0 && (
              <Alert severity="info">
                另有 {excludedCount} 个所选任务今天不在投放日期范围内，不会修改。
              </Alert>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <TimeField
                label="开始时间"
                format="HH:mm"
                value={start}
                onChange={setStart}
                sx={{width: 135}}
              />
              <Typography component="span">至</Typography>
              <TimeField
                label="结束时间"
                format="HH:mm"
                value={end}
                onChange={setEnd}
                sx={{width: 135}}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={saving}>取消</Button>
          <Button type="submit" disabled={invalid || saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default BatchTodayDaypartsDialog;
